import React from 'react';
import clsx from 'clsx';
import Box from '@mui/material/Box';

import { useAppProvider } from 'providers/app/app';
import { PaletteTypeButtonProps } from 'types/m3';
import { applyOpacityOnColor } from 'utils/colors';

import { M3ButtonBase } from './M3Button.styles';

type M3ButtonProps = PaletteTypeButtonProps & {
  active?: boolean;
};

const M3Button: React.FC<M3ButtonProps> = ({
  active,
  children,
  className,
  color,
  disabled,
  palette: paletteKey = 'main',
  sx,
  variant,
  ...restProps
}) => {
  const {
    palettes: { [paletteKey]: palette },
    isDarkMode,
  } = useAppProvider();

  const hasColorAndDisabled = !!color && disabled;

  if (variant === 'outlined') {
    let background: string = '';
    if (color === 'primary') {
      background = isDarkMode
        ? 'var(--md-ref-palette-primary80)'
        : 'var(--md-ref-palette-primary40)';
    } else if (color === 'secondary') {
      background = isDarkMode
        ? 'var(--md-ref-palette-secondary80)'
        : 'var(--md-ref-palette-secondary40)';
    }
    children = (
      <>
        <Box
          component='span'
          sx={{ background }}
          className='m3-button-outlined-background'
        />
        <Box
          flex={1}
          height='100%'
          display='flex'
          component='div'
          position='relative'
          alignItems='center'
          justifyContent='center'
        >
          {children}
        </Box>
      </>
    );
  }

  return (
    <M3ButtonBase
      color={color}
      disabled={disabled}
      variant={variant}
      sx={{
        '&.Mui-disabled': {
          color: `${applyOpacityOnColor(
            isDarkMode
              ? palette['md.ref.palette.neutral90']
              : palette['md.ref.palette.neutral10'],
            0.38,
          )} !important`,
          backgroundColor:
            variant === 'outlined'
              ? 'transparent !important'
              : hasColorAndDisabled
              ? `${applyOpacityOnColor(
                  isDarkMode
                    ? palette['md.ref.palette.neutral90']
                    : palette['md.ref.palette.neutral10'],
                  0.12,
                )} !important`
              : undefined,
          ...(isDarkMode
            ? {
                '.MuiSvgIcon-root': {
                  opacity: 0.3,
                  color: 'var(--md-sys-color-on-background-dark) !important',
                },
              }
            : null),
        },
        ...sx,
      }}
      className={clsx(
        {
          active,
          'dark-mode-button': isDarkMode,
        },
        className,
      )}
      {...restProps}
    >
      {children}
    </M3ButtonBase>
  );
};

export default M3Button;
